class TsDesktopSearch extends HTMLElement {
  connectedCallback() {
    if (this.input) return;
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-desktop-search-panel]');
    this.backdrop = this.querySelector('[data-desktop-search-backdrop]');
    this.products = this.querySelector('[role="listbox"]');
    this.defaults = this.products.innerHTML;
    this.titleElement = this.querySelector('[data-desktop-search-title]');
    this.count = this.querySelector('[data-desktop-search-count]');
    this.status = this.querySelector('[data-desktop-search-status]');
    this.clearButton = this.querySelector('[data-desktop-search-clear]');
    this.allLink = this.querySelector('[data-desktop-search-all]');
    this.allUrl = this.allLink.href;
    this.cache = new Map();
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.events = new AbortController();
    const listen = (target, name, handler) => target.addEventListener(name, handler, { signal: this.events.signal });
    listen(this.input, 'focus', () => { this.open(); this.search(); });
    listen(this.input, 'click', () => { if (!this.hasAttribute('open')) { this.open(); this.search(); } });
    listen(this.input, 'input', () => {
      this.open();
      this.clearSelection();
      this.clearButton.hidden = !this.input.value;
      this.request?.abort();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), 200);
    });
    listen(this.clearButton, 'click', (event) => {
      event.preventDefault();
      this.input.value = '';
      this.clearButton.hidden = true;
      this.input.focus({ preventScroll: true });
      this.open();
      this.search();
    });
    listen(this.input.form, 'submit', (event) => {
      const selected = this.products.querySelector('[aria-selected="true"]');
      if (selected) { event.preventDefault(); selected.click(); return; }
      if (!this.input.value.trim()) { event.preventDefault(); this.open(); return; }
      this.input.value = this.resolve(this.input.value.trim());
    });
    listen(this.querySelector('[data-desktop-search-close]'), 'click', () => this.close(true));
    listen(this.backdrop, 'click', () => this.close(true));
    listen(document, 'click', (event) => { if (!this.contains(event.target)) this.close(); });
    listen(this, 'focusout', () => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
    });
    listen(this, 'keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); this.close(true); }
      if (event.target !== this.input || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      this.open();
      const options = [...this.products.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      const previous = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      const next = previous < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) :
        (previous + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      this.clearSelection();
      const option = options[next];
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      const scroller = this.querySelector('.ts-desktop-search__body');
      const bounds = scroller.getBoundingClientRect(), rect = option.getBoundingClientRect();
      if (rect.top < bounds.top) scroller.scrollTop -= bounds.top - rect.top;
      else if (rect.bottom > bounds.bottom) scroller.scrollTop += rect.bottom - bounds.bottom;
    });
    listen(window, 'resize', () => { if (!this.desktop.matches) this.close(); else this.updateHeight(); });
    listen(window, 'scroll', () => { if (this.hasAttribute('open')) this.updateHeight(); });
  }

  disconnectedCallback() {
    this.events?.abort(); this.request?.abort();
    clearTimeout(this.timer); clearTimeout(this.focusTimer);
    this.motion?.cancel(); this.backdropMotion?.cancel();
    this.input = null;
  }

  resolve(term) {
    return customElements.get('predictive-search')?.resolveSearchTerm(term) || term;
  }

  updateHeight() {
    const bottom = document.querySelector('.section-header').getBoundingClientRect().bottom;
    this.style.setProperty('--ts-desktop-search-height', `${Math.max(180, window.innerHeight - bottom)}px`);
  }

  animate(opening) {
    const hidden = this.panel.hidden;
    const style = hidden ? null : getComputedStyle(this.panel);
    const from = { opacity: style?.opacity || '0', transform: style?.transform || 'translateY(-40px)' };
    const backdropOpacity = this.backdrop.hidden ? '0' : getComputedStyle(this.backdrop).opacity;
    this.motion?.cancel(); this.backdropMotion?.cancel();
    this.panel.hidden = false; this.backdrop.hidden = false;
    this.panel.inert = !opening;
    const finish = () => {
      if (!this.hasAttribute('open')) { this.panel.hidden = true; this.backdrop.hidden = true; }
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.panel.animate) { finish(); return; }
    const options = {
      duration: opening ? 460 : 260,
      easing: opening ? 'cubic-bezier(.16,.7,.3,1)' : 'cubic-bezier(.4,0,.2,1)',
      fill: 'both',
    };
    const motion = this.panel.animate([from, { opacity: opening ? 1 : 0, transform: opening ? 'translateY(0)' : 'translateY(-24px)' }], options);
    this.backdropMotion = this.backdrop.animate([{ opacity: backdropOpacity }, { opacity: opening ? 1 : 0 }], options);
    this.motion = motion;
    motion.onfinish = () => {
      if (this.motion !== motion) return;
      finish(); motion.cancel(); this.backdropMotion.cancel(); this.motion = null;
    };
  }

  open() {
    if (!this.desktop.matches || this.hasAttribute('open')) return;
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    document.querySelector('sticky-header')?.reveal();
    this.updateHeight(); this.animate(true);
  }

  close(restoreFocus = false) {
    if (!this.hasAttribute('open')) return;
    // Focus first so the focus listener cannot reopen a closing panel.
    if (restoreFocus) this.input.focus({ preventScroll: true });
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.request?.abort(); clearTimeout(this.timer);
    this.clearSelection(); this.animate(false);
  }

  clearSelection() {
    this.input.removeAttribute('aria-activedescendant');
    this.products.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  setResults(markup, label) {
    this.clearSelection(); this.products.innerHTML = markup;
    this.products.removeAttribute('aria-busy');
    this.count.textContent = label; this.status.textContent = label;
  }

  async search() {
    clearTimeout(this.timer); this.request?.abort();
    if (!this.hasAttribute('open')) return;
    const term = this.input.value.trim();
    const resolved = this.resolve(term);
    const url = new URL(this.input.form.action);
    url.searchParams.set('q', resolved); url.searchParams.set('options[prefix]', 'last');
    this.allLink.href = term ? url.href : this.allUrl;
    this.allLink.textContent = term ? 'Виж всички резултати →' : 'Разгледай всички часовници →';
    this.titleElement.textContent = term ? `Резултати за „${term}“` : 'Открий своя следващ часовник.';
    const brands = [...this.querySelectorAll('[data-search-brand]')];
    const matches = brands.filter((brand) => term && brand.textContent.toLowerCase().includes(resolved.toLowerCase()));
    brands.forEach((brand) => { brand.hidden = matches.length > 0 && !matches.includes(brand); });
    if (!term) { this.setResults(this.defaults, 'Избрани за теб'); return; }
    if (this.cache.has(resolved)) {
      const data = this.cache.get(resolved); this.setResults(data.html, data.label); return;
    }
    this.products.setAttribute('aria-busy', 'true');
    this.count.textContent = 'Търсене…'; this.status.textContent = 'Търсене…';
    const request = new AbortController(); this.request = request;
    try {
      const searchUrl = new URL(routes.predictive_search_url, location.origin);
      searchUrl.searchParams.set('q', resolved);
      searchUrl.searchParams.set('resources[type]', 'product');
      searchUrl.searchParams.set('resources[limit]', '8');
      searchUrl.searchParams.set('section_id', 'ts-desktop-search-results');
      const response = await fetch(searchUrl, { signal: request.signal });
      if (!response.ok) throw new Error('Search unavailable');
      const text = await response.text();
      if (request.signal.aborted || this.input.value.trim() !== term || !this.hasAttribute('open')) return;
      const results = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-desktop-product-results]');
      if (!results) throw new Error('Missing search results');
      const total = Number(results.dataset.resultCount);
      const data = { html: results.innerHTML, label: total ? `${total} предложения` : 'Няма предложения' };
      this.cache.set(resolved, data); this.setResults(data.html, data.label);
    } catch (error) {
      if (request.signal.aborted) return;
      this.setResults('<div class="ts-desktop-search__empty">Предложенията временно не са достъпни. Натисни Enter или „Виж всички резултати“.</div>', 'Търси във всички продукти');
    }
  }
}

customElements.define('ts-desktop-search', TsDesktopSearch);
